import { router } from 'expo-router';
import * as Notifications from 'expo-notifications';
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useLastNotificationResponseSafe } from './useLastNotificationResponseSafe';

// Show notifications even when the app is in the foreground.
if (Platform.OS !== 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
      shouldShowBanner: true,
      shouldShowList: true,
    }),
  });
}

// Decide where a tapped notification should land, based on the backend's
// `data.topic` contract:
//   - `promo`            → the event page for `data.event_id` (int)
//   - `booking_reminder` → the booking/ticket for `data.booking_id` (string
//                          UUID), falling back to the event page if it's absent
//   - anything else      → do nothing (admin one-off sends carry arbitrary
//                          `data`, often `{}`, with no actionable target)
// `topic` is only guaranteed present for the two automated types, and the field
// types differ (`event_id` int vs `booking_id` string), so we read each key
// defensively and never assume both are present.
function navigateToRoute(response: Notifications.NotificationResponse): void {
  const data = response.notification.request.content.data;

  // TODO(rehaul): the `/events/:id` and `/bookings/:id` routes were deleted with
  // the old app, so typed-routes no longer knows them. The navigate() targets
  // are cast to `any` to compile against the current (placeholder) route tree.
  // Re-point these to the new routes and drop the casts once the rehaul defines
  // the event/booking screens.
  switch (data?.topic) {
    case 'promo': {
      const eventId = data.event_id;
      if (eventId != null) {
        router.navigate(`/events/${eventId}` as any);
      }
      return;
    }
    case 'booking_reminder': {
      const bookingId = data.booking_id;
      if (typeof bookingId === 'string' && bookingId.length > 0) {
        router.navigate(`/bookings/${bookingId}` as any);
        return;
      }
      const eventId = data.event_id;
      if (eventId != null) {
        router.navigate(`/events/${eventId}` as any);
      }
      return;
    }
  }

  // Missing or unrecognized topic (e.g. admin one-off sends) — do nothing.
  // Never crash on absent keys.
}

export function useNotificationRouter(): void {
  const lastHandledId = useRef<string | null>(null);

  function handleOnce(response: Notifications.NotificationResponse): void {
    const id = response.notification.request.identifier;
    if (id === lastHandledId.current) return;
    lastHandledId.current = id;
    navigateToRoute(response);
  }

  // Handle notification taps while app is running
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const subscription =
      Notifications.addNotificationResponseReceivedListener(handleOnce);
    return () => subscription.remove();
  }, []);

  // Handle cold start -- check if app was opened from a notification.
  // Platform-split into .ts and .web.ts because the native hook is not
  // available on web.
  const lastResponse = useLastNotificationResponseSafe();
  useEffect(() => {
    if (lastResponse) {
      handleOnce(lastResponse);
    }
  }, [lastResponse]);
}
